import {ref, onUnmounted, computed, watchEffect} from 'vue';
import type {QScrollArea} from 'quasar';

interface DragToScrollReturn {
  scrollAreaRef: ReturnType<typeof ref<QScrollArea | null>>;
  onMouseDown(e: MouseEvent): void;
}

/** Enables mouse-drag horizontal scrolling on a Quasar QScrollArea, with keyboard focus management */
export function useDragToScroll(): DragToScrollReturn {
  const scrollAreaRef = ref<QScrollArea | null>(null);
  let isDragging = false;
  let startX = 0;
  let scrollLeft = 0;
  let didWalk = false;

  const scrollTarget = computed((): HTMLElement|null => {
    return scrollAreaRef.value?.getScrollTarget() as HTMLElement | null;
  });

  function onMouseDown(e: MouseEvent): void {
    isDragging = true;
    didWalk = false;
    startX = e.pageX;
    scrollLeft = scrollAreaRef.value!.getScrollPosition().left ?? 0;
    if (scrollTarget.value) {
      scrollTarget.value.style.cursor = 'grabbing';
    }
  }

  function onMouseMove(e: MouseEvent): void {
    if (!isDragging) { return; }
    e.preventDefault();
    const x = e.pageX;
    const walk = x - startX;
    if (Math.abs(walk) > 5) {
      didWalk = true;
      const allLinks = scrollTarget.value?.getElementsByTagName('a');
      if (allLinks) {
        Array.from(allLinks).forEach(link => {
          link.style.cursor = 'grabbing';
        });
      }
    }
    const el = scrollTarget.value;
    if (el) {
      const newScrollPos = scrollLeft - walk;
      scrollAreaRef.value!.setScrollPosition('horizontal', newScrollPos);
    }
  }

  function onMouseUp(): void {
    isDragging = false;
    if (scrollTarget.value) {
      scrollTarget.value.style.cursor = '';
      const allLinks = scrollTarget.value?.getElementsByTagName('a');
      if (allLinks) {
        Array.from(allLinks).forEach(link => {
          link.style.cursor = '';
        });
      }
    }
  }

  function onMouseClick(e: MouseEvent): void {
    if (e.target && e.target instanceof Node) {
      const isFocusWithinComponent = scrollTarget.value?.contains(e.target);
      if (didWalk && isFocusWithinComponent) {
        e.preventDefault();
        e.stopPropagation();
      }
    }
  }

  function onFocus(e: FocusEvent): void {
    if (e.target && e.target instanceof Node && scrollTarget.value) {
      const targetElement = e.target as HTMLElement;
      const isFocusWithinComponent = scrollTarget.value.contains(targetElement);
      const isKeyboardInUse = targetElement.matches(':focus-visible');

      if (isKeyboardInUse && isFocusWithinComponent) {
        const containerWidth = scrollAreaRef.value?.$el.offsetWidth as number; // eslint-disable-line @typescript-eslint/no-unsafe-member-access
        const childLeftPosition = targetElement.offsetLeft;
        const childRightPosition = childLeftPosition + targetElement.offsetWidth;

        if (childLeftPosition < (scrollAreaRef.value?.getScrollPosition().left ?? 0)) {
          scrollAreaRef.value?.setScrollPosition('horizontal', 0, 300);
        }

        if (childRightPosition > containerWidth) {
          const scrollTo = Math.abs(containerWidth - childRightPosition);
          scrollAreaRef.value?.setScrollPosition('horizontal', scrollTo + 10, 300);
        }
      }
    }
  }

  let listenersAttached = false;

  function addListeners(): void {
    if (listenersAttached) { return; }
    listenersAttached = true;
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('click', onMouseClick, true);
    window.addEventListener('focus', onFocus, true);
  }

  function removeListeners(): void {
    if (!listenersAttached) { return; }
    listenersAttached = false;
    window.removeEventListener('mousemove', onMouseMove);
    window.removeEventListener('mouseup', onMouseUp);
    window.removeEventListener('click', onMouseClick, true);
    window.removeEventListener('focus', onFocus, true);
  }

  // Attach listeners lazily once scrollAreaRef is bound
  watchEffect(() => {
    if (typeof window === 'undefined') { return; }
    if (scrollAreaRef.value) {
      addListeners();
    } else {
      removeListeners();
    }
  });

  onUnmounted(() => {
    removeListeners();
    document.body.style.cursor = '';
  });

  watchEffect(() => {
    if (scrollTarget.value) {
      scrollTarget.value.style.userSelect = 'none';
      const allLinks = scrollTarget.value.getElementsByTagName('a');
      if (allLinks) {
        Array.from(allLinks).forEach(link => {
          link.setAttribute('draggable', 'false');
        });
      }
    }
  });

  return {
    scrollAreaRef,
    onMouseDown,
  } as const;
}
